/**
 * useSEOMeta — Dynamically updates per-page <meta name="description">,
 * <link rel="canonical">, and Open Graph tags (og:title, og:description, og:url)
 * in the document head. Updates the existing static tags in index.html in place
 * rather than appending duplicates, and restores them on unmount.
 */
import { useEffect } from "react";
import { useDocumentTitle } from "./useDocumentTitle";

const BASE_URL = "https://goaldigger.dev";

interface SEOMetaOptions {
  /** Page-specific meta description (50–160 chars recommended). */
  description: string;
  /** Canonical path, e.g. "/support". Defaults to current pathname. */
  canonicalPath?: string;
  /** Optional og:title override. Defaults to current document.title. */
  ogTitle?: string;
  /** Optional absolute og:image / twitter:image URL (share-sized, ~1200x630). */
  ogImage?: string | null;
}

function setMeta(selector: string, attr: "content" | "href", value: string): () => void {
  const el = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (!el) return () => {};
  const prev = el.getAttribute(attr) || "";
  el.setAttribute(attr, value);
  return () => {
    if (prev) el.setAttribute(attr, prev);
  };
}

export function useSEOMeta({ description, canonicalPath, ogTitle, ogImage }: SEOMetaOptions) {
  useEffect(() => {
    const path = canonicalPath ?? window.location.pathname;
    const url = `${BASE_URL}${path}`;
    const title = ogTitle ?? document.title;

    const restorers = [
      setMeta('meta[name="description"]', "content", description),
      setMeta('link[rel="canonical"]', "href", url),
      setMeta('meta[property="og:url"]', "content", url),
      setMeta('meta[property="og:title"]', "content", title),
      setMeta('meta[property="og:description"]', "content", description),
      setMeta('meta[property="twitter:url"]', "content", url),
      setMeta('meta[property="twitter:title"]', "content", title),
      setMeta('meta[property="twitter:description"]', "content", description),
    ];
    if (ogImage && /^https:\/\//.test(ogImage)) {
      restorers.push(
        setMeta('meta[property="og:image"]', "content", ogImage),
        setMeta('meta[property="og:image:secure_url"]', "content", ogImage),
        setMeta('meta[name="twitter:image"]', "content", ogImage),
      );
    }

    return () => {
      restorers.forEach((r) => r());
    };
  }, [description, canonicalPath, ogTitle, ogImage]);
}
